<template>
  <section class="topic-highlights" aria-labelledby="home-topics-title">
    <header class="highlights-header">
      <div class="title-group">
        <span class="eyebrow">题材雷达</span>
        <h2 id="home-topics-title">{{ isHistorical ? '历史强势题材' : '今日强势题材' }}</h2>
        <span class="top-badge">TOP 3</span>
      </div>
      <div class="header-actions">
        <div class="history-navigator" aria-label="切换强势题材交易日">
          <b-button size="sm" variant="outline-secondary" :disabled="loading || !canGoOlder" title="前一交易日" aria-label="前一交易日" @click="goOlder">
            <b-icon icon="chevron-left"></b-icon>
          </b-button>
          <b-form-datepicker
            id="home-topic-history-datepicker"
            v-model="pickerDate"
            size="sm"
            locale="zh"
            :max="latestNavigableDate || undefined"
            :date-disabled-fn="isHistoryDateDisabled"
            :date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
            button-only
            right
            label-calendar="选择交易日"
            :disabled="loading"
            @input="onCalendarChange"
          ></b-form-datepicker>
          <span class="history-date">{{ navigationDateLabel }}</span>
          <b-button size="sm" variant="outline-secondary" :disabled="loading || !canGoNewer" title="后一交易日" aria-label="后一交易日" @click="goNewer">
            <b-icon icon="chevron-right"></b-icon>
          </b-button>
        </div>
        <span class="snapshot-label"><i></i>{{ snapshotLabel }}</span>
        <router-link class="library-link" to="/wonder/topic/table">全部题材 <span>→</span></router-link>
      </div>
    </header>

    <div class="snapshot-meta" aria-live="polite">
      <span v-if="data">{{ displayDate }}{{ isHistorical ? '收盘' : '行情' }} · {{ isHistorical ? '历史入库结果' : calculatedTime + '更新' }}</span>
      <span v-else>综合上涨强度、覆盖度与领涨表现</span>
      <button type="button" :disabled="loading" title="读取最新缓存" @click="fetchTopics">{{ loading ? '读取中…' : '刷新' }}</button>
    </div>
    <p v-if="error" class="ranking-notice" role="status">{{ error }}</p>
    <div v-if="loading && !data" class="ranking-empty">正在筛选题材，请稍候…</div>
    <div v-else-if="data && !data.items.length" class="ranking-empty-card">
      <div class="empty-status-banner">
        <div class="empty-badge">
          <span class="shield-icon">🛡️</span>
          <span>量化风控 · 盘面防守期</span>
        </div>
        <p class="empty-desc">
          当前市场处于分歧蓄势或退潮防守阶段，暂无板块同时满足<strong>领涨强度 &gt; 80%</strong>与<strong>行情完整度</strong>要求，模型启动主动风控防护，暂不盲目推荐追高题材。
        </p>
      </div>
      <div class="empty-quick-actions">
        <button type="button" class="quick-action-btn" @click="goOlder" :disabled="!canGoOlder">
          <span class="btn-icon">📅</span> 查看上一交易日强势题材
        </button>
        <router-link to="/wonder/topic/table" class="quick-action-link">
          进入全部题材全景库 <span>→</span>
        </router-link>
      </div>
    </div>

    <div v-if="data && data.items.length" :class="['topic-grid', 'topic-count-' + Math.min(data.items.length, 3)]">
      <article v-for="(topic, index) in data.items" :key="topic.unique_key" class="topic-highlight" :class="{ 'topic-first': index === 0 }">
        <div class="topic-topline">
          <div class="topic-heading">
            <span class="topic-rank"><b>0{{ index + 1 }}</b></span>
            <h3 :title="topic.name"><router-link :to="topicLink(topic)" @click.native="track(topic)">{{ displayTopicName(topic.name) }}</router-link></h3>
            <button v-if="isLoggedIn && topic.id" type="button" :class="['follow-button', { active: isFollowed(topic.id) }]" @click="toggleFollow(topic)">
              {{ isFollowed(topic.id) ? '已关注' : '+ 关注' }}
            </button>
          </div>
          <div class="topline-right">
            <span
              v-if="topic.mainline"
              :class="['mainline-badge', 'badge-' + (topic.mainline.level || 'rotation')]"
              :title="(topic.mainline.description || '') + (topic.mainline.combined_label ? ' · ' + topic.mainline.combined_label : (topic.mainline.stage_tag ? ' · ' + topic.mainline.stage_tag : ''))"
            >
              {{ topic.mainline.tag || '盘中热点' }}
              <small v-if="topic.mainline.continuity_score > 0"> {{ formatNumber(topic.mainline.continuity_score, 0) }}分</small>
            </span>
            <span class="strength-score">强度 <b>{{ formatNumber(topic.score, 1) }}</b></span>
          </div>
        </div>
        <div class="topic-mainline">
          <strong :class="['topic-return', changeClass(topic.today_pct)]">{{ signedNumber(topic.today_pct, 2) }}<small>%</small></strong>
          <div class="topic-metrics">
            <span>上涨 <b>{{ topic.up_ratio }}%</b><small>（{{ topic.up_count }}/{{ topic.stock_count }}）</small></span>
            <span>领涨 <b>{{ topic.leader_count }}</b></span>
            <span>涨停 <b>{{ topic.limit_up_count }}</b></span>
            <span>跌停 <b>{{ topic.limit_down_count || 0 }}</b></span>
          </div>
        </div>
        <div class="breadth-track" :aria-label="'上涨占比 ' + topic.up_ratio + '%'" role="img"><span :style="{ width: topic.up_ratio + '%' }"></span></div>

        <!-- 核心龙头股展示区 -->
        <div v-if="topic.core_stocks && topic.core_stocks.length" class="topic-cores">
          <span class="core-label">核心股</span>
          <div class="core-items">
            <div
              v-for="stk in topic.core_stocks"
              :key="stk.code"
              class="core-tag"
              :title="stk.name + ' (' + stk.code + ') · ' + (stk.role || '')"
            >
              <span class="stk-role" v-if="stk.role">{{ stk.role }}</span>
              <span class="stk-name">{{ stk.name }}</span>
              <span :class="['stk-pct', changeClass(stk.pct)]">{{ signedNumber(stk.pct, 2) }}%</span>
              <span v-if="stk.is_limit" class="stk-limit">封板</span>
            </div>
          </div>
        </div>
      </article>
    </div>
    <details class="ranking-method">
      <summary>如何选出这三个题材与核心股？</summary>
      <p>综合分 = 平均涨幅（40 分，5% 封顶）+ 上涨占比（20 分）+ 涨幅超过 5% 的占比（20 分，30% 封顶）+ 涨停占比（20 分，10% 封顶）。至少 5 家有效行情、覆盖至少 80% 成分股、平均涨幅为正且至少半数上涨才入选。</p>
      <p><strong>🔥真·主线认证</strong>：根据题材持续性量化战法（v6），仅当题材持续性评分 ≥75 分、近 3 日至少 2 天稳居前列、且处于“延续增强/持续稳定”的主升阶段时点亮金牌徽标，并精选核心龙头身位股；单日短线脉冲题材标识为“日内脉冲”或“盘中轮动”，请警惕追高分化风险。</p>
      <p>排名反映当前强度，不是收益预测或买入信号。</p>
    </details>
  </section>
</template>

<script>
import { request_zzquant } from '@/network/request'
import { trackProductEvent } from '@/utils/productAnalytics'

export default {
  name: 'HomeTopicHighlights',
  data() {
    return { data: null, loading: false, error: '', timer: null, disposed: false, historyDates: [], selectedDate: '', pickerDate: '', liveTradeDate: '', requestVersion: 0, followedTopicIds: [], lastImpressionKey: '' }
  },
  computed: {
    isHistorical() {
      return Boolean(this.selectedDate)
    },
    latestNavigableDate() {
      return this.liveTradeDate || this.historyDates[0] || ''
    },
    navigationDate() {
      return this.selectedDate || this.liveTradeDate || (this.data && this.data.trade_date) || this.historyDates[0] || ''
    },
    navigationDateLabel() {
      if (!this.navigationDate) return '选择日期'
      return this.navigationDate.slice(5).replace('-', '/') + (this.isHistorical ? '' : ' 实时')
    },
    navigationDateIndex() {
      return this.historyDates.indexOf(this.navigationDate)
    },
    canGoOlder() {
      if (this.navigationDateIndex < 0) return this.historyDates.length > 0
      return this.navigationDateIndex < this.historyDates.length - 1
    },
    canGoNewer() {
      return Boolean(this.selectedDate) && this.navigationDateIndex >= 0
    },
    snapshotLabel() {
      if (!this.data) return '等待行情'
      if (this.error) return '上次成功获取的排名'
      if (this.isHistorical) return '历史收盘强度排名'
      if (this.data.cache_status === 'warming_up') return '等待竞价后的首次题材更新'
      if (this.data.source === 'auction') return '竞价行情综合排名'
      return this.data.is_trading ? '盘中综合排名 · 跟随市场情绪每 5 分钟更新' : '今日收盘排名'
    },
    calculatedTime() {
      if (!this.data || !this.data.calculated_at) return '暂无'
      const time = new Date(this.data.calculated_at)
      return Number.isNaN(time.getTime()) ? '未知' : time.toLocaleTimeString('zh-CN', { timeZone: 'Asia/Shanghai', hour12: false })
    },
    displayDate() {
      if (!this.data || !this.data.trade_date) return '今日'
      return this.data.trade_date.slice(5).replace('-', '/')
    },
    emptyMessage() {
      if (this.isHistorical) return '该交易日没有满足上涨覆盖与行情完整度要求的题材。'
      if (this.data && this.data.cache_status === 'warming_up') return '今日题材排名将在竞价行情更新后生成。'
      return '当前没有满足上涨覆盖与行情完整度要求的题材，暂不强行推荐。稍后再来看看。'
    },
    isLoggedIn() {
      try { return Boolean(localStorage.getItem('token')) } catch (e) { return false }
    }
  },
  mounted() {
    this.fetchHistoryDates()
    this.fetchTopics()
    document.addEventListener('visibilitychange', this.onVisibility)
    this.$root.$on('topic-follow-status-changed', this.fetchFollowStatus)
  },
  beforeDestroy() {
    this.disposed = true
    clearTimeout(this.timer)
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.$root.$off('topic-follow-status-changed', this.fetchFollowStatus)
  },
  methods: {
    topicLink(topic) { return { path: '/wonder/topic/detail/' + encodeURIComponent(topic.unique_key), query: { source: 'home_top3' } } },
    displayTopicName(name) {
      return String(name || '').replace(/\s*[（(][^（）()]*[）)]/g, '').trim()
    },
    formatNumber(value, digits) {
      const number = Number(value)
      return Number.isFinite(number) ? number.toFixed(digits) : '--'
    },
    signedNumber(value, digits) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '--'
      return `${number > 0 ? '+' : ''}${number.toFixed(digits)}`
    },
    changeClass(value) {
      const number = Number(value)
      return number > 0 ? 'is-up' : number < 0 ? 'is-down' : 'is-flat'
    },
    track(topic) {
      if (window._hmt) window._hmt.push(['_trackEvent', '首页题材', '查看分析', topic.unique_key])
      trackProductEvent('home_topic_click', { topic_id: topic.id, source: 'home_top3', metadata: { trade_date: this.data && this.data.trade_date, rank: topic.rank } })
    },
    isFollowed(topicId) {
      return this.followedTopicIds.includes(Number(topicId))
    },
    async fetchFollowStatus() {
      if (!this.isLoggedIn || !this.data || !this.data.items.length) return
      const ids = this.data.items.map(item => item.id).filter(Boolean)
      if (!ids.length) return
      try {
        const res = await request_zzquant({ url: '/v3/topic/follows/status', params: { topic_ids: ids.join(',') }, noRedirect: true })
        const body = res && res.data
        if (body && body.code === 20000) this.followedTopicIds = (body.data.followed_topic_ids || []).map(Number)
      } catch (e) { /* 关注状态不影响首页排名 */ }
    },
    async toggleFollow(topic) {
      if (!topic.id) return
      const followed = this.isFollowed(topic.id)
      try {
        await request_zzquant({ url: `/v3/topic/follows/${topic.id}`, method: followed ? 'DELETE' : 'POST' })
        this.followedTopicIds = followed ? this.followedTopicIds.filter(id => id !== Number(topic.id)) : this.followedTopicIds.concat(Number(topic.id))
        this.$root.$emit('topic-follow-status-changed')
        this.$bvToast.toast(followed ? '已取消关注' : '已加入我的题材', { variant: followed ? 'secondary' : 'success', solid: true, autoHideDelay: 1600 })
      } catch (e) { /* 全局请求层处理登录 */ }
    },
    onVisibility() {
      clearTimeout(this.timer)
      if (!document.hidden) this.fetchTopics()
    },
    applyHistoryDate(date) {
      const target = date || this.latestNavigableDate
      this.selectedDate = target && target !== this.liveTradeDate ? target : ''
      this.pickerDate = target
      clearTimeout(this.timer)
      this.error = ''
      this.data = null
      this.fetchTopics()
    },
    goOlder() {
      if (!this.canGoOlder) return
      this.applyHistoryDate(this.navigationDateIndex < 0 ? this.historyDates[0] : this.historyDates[this.navigationDateIndex + 1])
    },
    goNewer() {
      if (!this.canGoNewer) return
      const nextIndex = this.navigationDateIndex - 1
      this.applyHistoryDate(nextIndex >= 0 ? this.historyDates[nextIndex] : this.liveTradeDate)
    },
    onCalendarChange(date) {
      if (date) this.applyHistoryDate(date)
    },
    isHistoryDateDisabled(ymd) {
      return !this.historyDates.includes(ymd) && ymd !== this.liveTradeDate
    },
    async fetchHistoryDates() {
      try {
        const response = await request_zzquant({ url: '/v3/topic/home-topics/history-dates?limit=60', noRedirect: true })
        const body = response && response.data
        if (body && body.code === 20000 && Array.isArray(body.data)) {
          this.historyDates = body.data
          if (!this.pickerDate) this.pickerDate = this.liveTradeDate || this.historyDates[0] || ''
        }
      } catch (e) {
        this.historyDates = []
      }
    },
    async fetchTopics() {
      if (this.disposed || document.hidden) return
      clearTimeout(this.timer)
      const requestVersion = ++this.requestVersion
      const requestedDate = this.selectedDate
      this.loading = true
      try {
        const query = requestedDate ? `?date=${encodeURIComponent(requestedDate)}` : ''
        const response = await request_zzquant({ url: '/v3/topic/home-topics' + query, noRedirect: true, timeout: 60000 })
        const body = response && response.data
        if (!body || body.code !== 20000 || !body.data || !Array.isArray(body.data.items)) throw new Error('排名数据不可用')
        if (this.disposed || requestVersion !== this.requestVersion) return
        this.data = body.data
        if (!requestedDate) this.liveTradeDate = body.data.trade_date || this.liveTradeDate
        this.pickerDate = requestedDate || body.data.trade_date || this.pickerDate
        this.error = ''
        this.fetchFollowStatus()
        const impressionKey = `${body.data.trade_date}:${body.data.items.map(item => item.id).join(',')}:${Boolean(requestedDate)}`
        if (impressionKey !== this.lastImpressionKey) {
          this.lastImpressionKey = impressionKey
          trackProductEvent('home_topic_impression', {
            source: 'home_top3',
            metadata: { trade_date: body.data.trade_date, topic_ids: body.data.items.map(item => item.id), historical: Boolean(requestedDate) }
          })
        }
      } catch (e) {
        if (!this.disposed && requestVersion === this.requestVersion) this.error = this.data ? '更新暂时失败，以下保留上次排名，请留意行情日期和计算时间。' : '暂时无法获取题材排名，请稍后重试，或进入题材库查看。'
      } finally {
        if (requestVersion === this.requestVersion) {
          this.loading = false
          if (!this.disposed && !document.hidden && !this.selectedDate) this.timer = setTimeout(this.fetchTopics, ((this.data && this.data.refresh_seconds) || 60) * 1000)
        }
      }
    }
  }
}
</script>

<style scoped>
.topic-highlights { --topic-bg:#fff; --topic-ink:#202d3c; --topic-muted:#647184; --topic-border:#e2e7ed; --topic-accent:#bf4b38; margin:10px 0 14px; color:var(--topic-ink); }
.highlights-header { min-height:34px; display:flex; align-items:center; justify-content:space-between; gap:16px; }
.title-group,.header-actions,.snapshot-label,.topic-topline,.topic-heading,.topic-rank,.topic-mainline,.topic-metrics { display:flex; align-items:center; }
.title-group { gap:9px; min-width:0; }.eyebrow { padding:3px 7px; border-radius:4px; background:#f5e9e6; color:var(--topic-accent); font-size:11px; font-weight:700; white-space:nowrap; }
h2 { font-size:20px; line-height:1.2; font-weight:700; margin:0; }.top-badge { color:var(--topic-accent); font-size:11px; font-weight:700; }
.header-actions { gap:16px; font-size:12px; white-space:nowrap; }.snapshot-label { gap:6px; color:var(--topic-muted); }.snapshot-label i { width:6px; height:6px; background:#42a66a; border-radius:50%; }
.history-navigator { height:27px; display:flex; align-items:center; overflow:hidden; border:1px solid var(--topic-border); border-radius:5px; background:var(--topic-bg); }
.history-navigator > .btn { width:26px; height:25px; padding:0; border:0; border-radius:0; color:var(--topic-muted); }
.history-navigator .b-form-btn-label-control.form-control { width:27px; min-height:25px; height:25px; padding:0; border:0; border-radius:0; background:transparent; }
.history-navigator .b-form-btn-label-control.form-control > .btn { width:26px; padding:0; color:#47749e; }
.history-date { min-width:72px; padding:0 4px; color:var(--topic-ink); font-size:11px; text-align:center; font-variant-numeric:tabular-nums; }
.library-link { font-weight:600; }.library-link span { margin-left:3px; }
.snapshot-meta { height:30px; display:flex; align-items:center; gap:10px; font-size:11px; color:var(--topic-muted); }
.snapshot-meta button { background:none; border:0; color:#47749e; padding:2px 4px; font-size:11px; cursor:pointer; }.snapshot-meta button:disabled { opacity:.5; cursor:default; }
.topic-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:10px; }
.topic-grid.topic-count-1 { grid-template-columns:minmax(0,1fr); }
.topic-grid.topic-count-2 { grid-template-columns:repeat(2,minmax(0,1fr)); }
.topic-grid.topic-count-1 .topic-highlight { display:grid; grid-template-columns:minmax(320px,1fr) auto; align-items:center; gap:4px 22px; }
.topic-grid.topic-count-1 .topic-topline { height:auto; }
.topic-grid.topic-count-1 .topic-mainline { margin:0; }
.topic-grid.topic-count-1 .breadth-track { grid-column:1/-1; width:100%; margin-top:2px; }
.topic-highlight { background:var(--topic-bg); border:1px solid var(--topic-border); border-radius:9px; padding:10px 13px 7px; min-width:0; overflow:hidden; }
.topic-first { box-shadow:inset 3px 0 0 var(--topic-accent); }
.topic-topline { justify-content:space-between; gap:8px; height:24px; min-width:0; }
.topline-right { display:flex; align-items:center; gap:6px; flex:none; }
.mainline-badge { font-size:10px; font-weight:700; padding:1px 6px; border-radius:10px; white-space:nowrap; }
.mainline-badge.badge-mainline { background:linear-gradient(135deg, #ff4d4f, #fa8c16); color:#fff; box-shadow:0 1px 4px rgba(250,140,22,0.35); }
.mainline-badge.badge-trend { background:#e6f7ff; color:#096dd9; border:1px solid #91d5ff; }
.mainline-badge.badge-pulse { background:#fff7e6; color:#d46b08; border:1px solid #ffd591; }
.mainline-badge.badge-rotation { background:#f5f5f5; color:#8c8c8c; }
.mainline-badge small { opacity:0.9; margin-left:2px; font-size:9px; font-weight:600; }
.topic-cores { display:flex; align-items:center; gap:6px; margin-top:8px; padding-top:6px; border-top:1px dashed var(--topic-border); font-size:11px; }
.core-label { flex:none; color:var(--topic-muted); font-size:10px; font-weight:600; }
.core-items { display:flex; align-items:center; gap:6px; overflow-x:auto; scrollbar-width:none; flex:1; min-width:0; }
.core-items::-webkit-scrollbar { display:none; }
.core-tag { display:inline-flex; align-items:center; gap:4px; padding:2px 6px; background:#f8fafc; border:1px solid var(--topic-border); border-radius:4px; font-size:11px; white-space:nowrap; }
.stk-role { font-size:9px; font-weight:600; color:#bf4b38; background:#fdf0ed; padding:0 3px; border-radius:2px; }
.stk-name { font-weight:600; color:var(--topic-ink); }
.stk-pct { font-weight:700; font-variant-numeric:tabular-nums; }
.stk-pct.is-up { color:#c84435; }
.stk-pct.is-down { color:#269c50; }
.stk-pct.is-flat { color:var(--topic-muted); }
.stk-limit { font-size:9px; background:#c84435; color:#fff; padding:0 3px; border-radius:2px; font-weight:700; }
.topic-heading { gap:7px; min-width:0; }.topic-rank { flex:none; color:var(--topic-muted); font-size:10px; font-weight:600; }.topic-rank b { color:#738093; font-size:12px; }.topic-first .topic-rank,.topic-first .topic-rank b { color:var(--topic-accent); }
.follow-button { flex:none; padding:1px 6px; border:1px solid #ccd5df; border-radius:10px; background:transparent; color:#647184; font-size:10px; cursor:pointer; }.follow-button.active { border-color:#d8a398; background:#f7ece9; color:#ad4635; }
.strength-score { padding:2px 6px; border-radius:10px; background:#f3f5f7; color:var(--topic-muted); font-size:10px; font-variant-numeric:tabular-nums; }.strength-score b { color:var(--topic-ink); font-size:12px; }
.topic-mainline { justify-content:space-between; gap:12px; margin:7px 0 4px; min-width:0; }
h3 { min-width:0; margin:0; font-size:16px; font-weight:700; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }h3 a { color:inherit; }
.topic-return { flex:none; font-size:24px; line-height:1; font-variant-numeric:tabular-nums; }.topic-return.is-up { color:#c84435; }.topic-return.is-down { color:#269c50; }.topic-return.is-flat { color:var(--topic-muted); }.topic-return small { font-size:14px; }
.topic-metrics { gap:18px; color:var(--topic-muted); font-size:11px; }.topic-metrics b { margin-left:3px; color:var(--topic-ink); font-size:13px; font-variant-numeric:tabular-nums; }.topic-metrics small { margin-left:2px; font-size:10px; }
.breadth-track { height:3px; margin:7px 0 2px; background:var(--topic-border); border-radius:4px; overflow:hidden; }.breadth-track span { display:block; height:100%; background:#c16b5a; }
.ranking-method { margin-top:6px; font-size:10px; color:var(--topic-muted); line-height:1.6; }.ranking-method summary { display:inline-block; cursor:pointer; }.ranking-method p { max-width:1100px; margin:6px 0; }
.ranking-notice,.ranking-empty { padding:12px; background:var(--topic-bg); border:1px solid var(--topic-border); border-radius:8px; font-size:12px; }
@media(max-width:900px) { .topic-grid { display:flex; gap:8px; overflow-x:auto; scroll-snap-type:x mandatory; padding-bottom:3px; scrollbar-width:none; }.topic-grid::-webkit-scrollbar { display:none; }.topic-highlight { flex:0 0 calc(50% - 4px); padding:10px; scroll-snap-align:start; }.topic-grid.topic-count-1 .topic-highlight { display:block; flex-basis:100%; }.topic-grid.topic-count-1 .topic-mainline { margin:7px 0 4px; }.topic-metrics { gap:10px; }.topic-return { font-size:21px; } }
@media(max-width:650px) { .topic-highlights { margin-top:8px; }.highlights-header { align-items:flex-start; }.title-group { gap:6px; }h2 { font-size:18px; }.snapshot-label { display:none; }.header-actions { gap:8px; }.history-date { min-width:62px; }.snapshot-meta { justify-content:space-between; }.topic-highlight { flex-basis:84vw; }.ranking-method { margin-top:3px; } }

.ranking-empty-card {
  padding: 16px 20px;
  background: var(--topic-bg);
  border: 1px solid var(--topic-border);
  border-radius: 9px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 1px 4px rgba(30, 45, 65, .05);
}
.empty-status-banner {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}
.empty-badge {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 20px;
  background: rgba(217, 119, 6, 0.12);
  border: 1px solid rgba(217, 119, 6, 0.25);
  color: #d97706;
  font-size: 12px;
  font-weight: 700;
  white-space: nowrap;
}
.empty-desc {
  margin: 0;
  font-size: 12px;
  color: var(--topic-muted);
  line-height: 1.5;
  flex: 1;
  min-width: 240px;
}
.empty-desc strong {
  color: var(--topic-ink);
}
.empty-quick-actions {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
  padding-top: 10px;
  border-top: 1px dashed var(--topic-border);
}
.quick-action-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 12px;
  border-radius: 6px;
  border: 1px solid #47749e;
  background: transparent;
  color: #47749e;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
}
.quick-action-btn:hover:not(:disabled) {
  background: #47749e;
  color: #fff;
}
.quick-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.quick-action-link {
  font-size: 11px;
  font-weight: 600;
  color: #47749e;
  text-decoration: none;
}
.quick-action-link:hover {
  text-decoration: underline;
}
.theme-dark .empty-badge { background: rgba(245, 158, 11, 0.18); border-color: rgba(245, 158, 11, 0.35); color: #fbbf24; }
</style>

<style>
.theme-dark .topic-highlights { --topic-bg:#202734; --topic-ink:#e6ebf2; --topic-muted:#acb6c5; --topic-border:#3b4554; }.theme-dark .topic-highlights .eyebrow { background:#412e2e; }.theme-dark .topic-highlights .strength-score { background:#2b3442; }
.theme-dark .mainline-badge.badge-trend { background:#112a45; color:#69c0ff; border-color:#1765ad; }
.theme-dark .mainline-badge.badge-pulse { background:#382410; color:#ffc069; border-color:#874d00; }
.theme-dark .mainline-badge.badge-rotation { background:#2a313d; color:#8c9ba5; }
.theme-dark .core-tag { background:#1e2632; border-color:#3b4554; }
.theme-dark .stk-role { background:#3b2323; color:#ff7875; }
.theme-dark .empty-badge { background: rgba(245, 158, 11, 0.18); border-color: rgba(245, 158, 11, 0.35); color: #fbbf24; }
</style>
